import ScreenWrapper from '@components/layout/screen-wrapper';
import { Button, Card, Stack, Text } from '@components/ui';
import { ApiUrl } from '@config/apiUrl';
import { useHireJob } from '@features/buyer/hire/hire-job-context';
import VerifyCoupon from '@features/buyer/hire/verify-coupon/verify-coupon';
import { CardFields } from '@features/payments/card-fields';
import {
  hostedFieldStyles,
} from '@features/payments/hostedFields';
import apiProtected from '@lib/api/apiClient';
import { useCreateJobMutation } from '@lib/api/jobs';
import { logError } from '@lib/logError';
import Backdrop from '@mui/material/Backdrop';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { alpha } from '@mui/material/styles';
import braintree, { type BraintreeHostedFields } from 'braintree-web';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import type {
  ApiEnvelope,
  CouponDiscountResponse,
  DiscountResult,
} from './submitPaymentTypes';

const sectionPadding = { px: { xs: 2.5, sm: 3.5 }, py: 2.5 };


const SubmitPayment = (): JSX.Element => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { createJob, saveJob } = useHireJob();

  const [openModal, setOpenModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const createJobRequest = useCreateJobMutation();
  const [clientToken, setClientToken] = useState<string | null>(null);
  const [discountData, setDiscountData] = useState<number | string | null>(
    null
  );
  const [discountCriteria, setDiscountCriteria] = useState<string | null>(null);
  const [isReady, setIsReady] = useState(false);

  const hostedFieldsRef = useRef<BraintreeHostedFields | null>(null);

  useEffect(() => {
    (async () => {
      const res = await apiProtected.get<ApiEnvelope<string>>(
        `${ApiUrl.server_url}v1/braintree/get-client-token`
      );
      setClientToken(res.data.data ?? null);
    })();
  }, []);

  useEffect(() => {
    if (!createJob?.buyerCost) return;
    apiProtected
      .get<ApiEnvelope<DiscountResult>>(
        `${ApiUrl.server_url}v1/discounts/calculate-discount-for-buyer?buyerCost=${createJob.buyerCost}`
      )
      .then((res) => {
        const finalBuyerCost = res?.data?.data?.finalBuyerCost;
        if (finalBuyerCost) {
          saveJob({ ...createJob, discountedBuyerCost: finalBuyerCost });
          setDiscountData(finalBuyerCost);
          setDiscountCriteria(res.data.data?.discountCriteria ?? null);
        }
      })
      .catch((error: unknown) => logError('payment fields', error));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createJob?.buyerCost]);

  useEffect(() => {
    if (!clientToken) return;

    let hostedFields: BraintreeHostedFields | undefined;
    braintree.client
      .create({ authorization: clientToken })
      .then((clientInstance) =>
        braintree.hostedFields.create({
          client: clientInstance,
          styles: hostedFieldStyles(),
          fields: {
            number: {
              container: '#bt-card-number',
              placeholder: '•••• •••• •••• ••••',
            },
            expirationDate: { container: '#bt-expiry', placeholder: 'MM / YY' },
            cvv: { container: '#bt-cvv', placeholder: '•••' },
            cardholderName: {
              container: '#bt-name',
              placeholder: 'Name on card',
            },
          },
        })
      )
      .then((hostedFieldsInstance) => {
        hostedFields = hostedFieldsInstance;
        hostedFieldsRef.current = hostedFieldsInstance;

        hostedFieldsInstance.on('validityChange', () => {
          const state = hostedFieldsInstance.getState();
          const allValid = Object.values(state.fields).every(
            (field) => field.isValid
          );
          setIsReady(allValid);
        });
      })
      .catch((error: unknown) => logError('payment fields', error));

    return () => {
      hostedFields?.teardown();
      hostedFieldsRef.current = null;
    };
  }, [clientToken]);

  const handleCouponDiscountAmount = (res: CouponDiscountResponse): void => {
    if (res) {
      const finalBuyerCost = res?.data?.data?.finalBuyerCost;
      saveJob({ ...createJob, discountedBuyerCost: finalBuyerCost });
      setDiscountData(finalBuyerCost ?? null);
      setDiscountCriteria(res?.data?.data?.discountCriteria ?? null);
    }
  };

  const handleSubmitPayment = async (): Promise<void> => {
    if (!hostedFieldsRef.current) return;
    try {
      setLoading(true);
      const { nonce } = await hostedFieldsRef.current.tokenize();
      const body = { ...createJob, nonce };
      const { data } = await createJobRequest.mutateAsync(body);
      setLoading(false);
      toast.success(data.message ?? '');
      navigate('/');
    } catch (error: unknown) {
      setLoading(false);
      const message =
        typeof error === 'object' && error !== null && 'response' in error
          ? (error as { response?: { data?: { message?: string } } }).response
              ?.data?.message
          : undefined;
      toast.error(message ?? 'Payment failed');
    }
  };

  const displayCost = discountData ?? createJob?.buyerCost;

  return (
    <ScreenWrapper
      pageSx={{ overflowY: 'auto' }}
      containerSx={{
        display: 'flex',
        minHeight: '100%',
        flexDirection: 'column',
        alignItems: 'center',
        px: 2,
        pb: 6,
        pt: 4,
      }}
    >

      {!clientToken ? (
        <Backdrop
          sx={{
            color: 'text.primary',
            zIndex: (theme) => theme.zIndex.drawer + 1,
            position: 'absolute',
          }}
          open
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      ) : (
        <Card sx={{ width: '100%', maxWidth: 520, overflow: 'hidden', p: 0 }}>
          <Box
            sx={{ ...sectionPadding, borderBottom: 1, borderColor: 'divider' }}
          >
            <Text variant="overline" tone="muted">
              Hire a R8TR — Payment
            </Text>
          </Box>

          <Stack
            gap="sm"
            sx={{ ...sectionPadding, borderBottom: 1, borderColor: 'divider' }}
          >
            {createJob?.contactName ? (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'baseline',
                  justifyContent: 'space-between',
                  gap: 2,
                }}
              >
                <Text
                  variant="label"
                  tone="muted"
                  sx={{
                    flexShrink: 0,
                    fontSize: 11,
                    textTransform: 'uppercase',
                    letterSpacing: '0.8px',
                  }}
                >
                  {t('hire.contact')}
                </Text>
                <Text sx={{ textAlign: 'right', fontSize: 14 }}>
                  {createJob.contactName}
                </Text>
              </Box>
            ) : null}
            {createJob?.description ? (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'baseline',
                  justifyContent: 'space-between',
                  gap: 2,
                }}
              >
                <Text
                  variant="label"
                  tone="muted"
                  sx={{
                    flexShrink: 0,
                    fontSize: 11,
                    textTransform: 'uppercase',
                    letterSpacing: '0.8px',
                  }}
                >
                  {t('hire.vehicle')}
                </Text>
                <Text sx={{ textAlign: 'right', fontSize: 14 }}>
                  {createJob.description}
                </Text>
              </Box>
            ) : null}
          </Stack>

          <Box
            sx={{
              ...sectionPadding,
              py: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1.5,
              borderBottom: 1,
              borderColor: 'divider',
            }}
          >
            <Text
              variant="label"
              tone="muted"
              sx={{
                fontSize: 11,
                textTransform: 'uppercase',
                letterSpacing: '0.8px',
              }}
            >
              {t('hire.total')}
            </Text>
            <Stack align="end" gap="xs">
              {discountData ? (
                <Text
                  variant="caption"
                  tone="muted"
                  sx={{ fontSize: 13, textDecoration: 'line-through' }}
                >
                  ${createJob?.buyerCost}
                </Text>
              ) : null}
              <Text
                tone="primary"
                sx={{ fontSize: 26, fontWeight: 700, lineHeight: 1 }}
              >
                ${displayCost}
              </Text>
              {discountCriteria ? (
                <Box
                  component="span"
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    borderRadius: 20,
                    border: 1,
                    borderColor: 'primary.main',
                    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
                    px: 1.25,
                    py: '3px',
                    fontSize: 11,
                    fontWeight: 600,
                    color: 'primary.main',
                  }}
                >
                  {discountCriteria}
                </Box>
              ) : null}
            </Stack>
          </Box>

          {!discountData ? (
            <Box
              sx={{
                ...sectionPadding,
                py: 1.75,
                borderBottom: 1,
                borderColor: 'divider',
              }}
            >
              <Button
                variant="outlined"
                fullWidth
                onClick={() => setOpenModal(true)}
                sx={{
                  borderStyle: 'dashed',
                  textTransform: 'uppercase',
                  letterSpacing: '0.8px',
                }}
              >
                {t('hire.applyCoupon')}
              </Button>
            </Box>
          ) : null}

          <CardFields sx={{ ...sectionPadding, pt: 3, pb: 2.5 }} />

          <Box sx={{ ...sectionPadding, pt: 0, pb: 3.5 }}>
            <Button
              variant="primary"
              fullWidth
              disabled={!isReady}
              onClick={handleSubmitPayment}
              sx={{
                textTransform: 'uppercase',
                letterSpacing: '1px',
                py: 1.75,
              }}
            >
              {t('hire.submitPayment')}
            </Button>
          </Box>
        </Card>
      )}

      <Backdrop
        sx={{
          color: 'text.primary',
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      {openModal && (
        <VerifyCoupon
          open={openModal}
          cost={createJob?.buyerCost}
          handleAddCouponModal={() => setOpenModal(false)}
          handleCouponDiscountAmount={handleCouponDiscountAmount}
        />
      )}
    </ScreenWrapper>
  );
};

export default SubmitPayment;
